import React, { useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getSimpleProductKeywordHistoryAPI } from "../../../../api/index";
import "./SimpleKeywordRankHistoryModal.css";

const getColorByIndex = (index, totalLength) => {
  if (totalLength <= 1) return "#10b981";
  const indexRatio = index / (totalLength - 1);
  if (indexRatio < 0.25) return "#10b981";
  if (indexRatio < 0.5) return "#fbbf24";
  if (indexRatio < 0.75) return "#f97316";
  return "#ef4444";
};

const calculateYAxisRange = (rankData) => {
  if (!rankData.length) return { min: 1, max: 100, stepSize: 10 };

  if (rankData.length === 1) {
    const singleRank = rankData[0];
    if (singleRank === 500) {
      return { min: 450, max: 500, stepSize: 10 };
    }
    const margin = Math.max(10, Math.ceil(singleRank * 0.2));
    const min = Math.max(1, singleRank - margin);
    const max = singleRank + margin;
    const stepSize = Math.max(1, Math.ceil((max - min) / 5));
    return { min, max, stepSize };
  }

  const maxRank = Math.max(...rankData);
  const minRank = Math.min(...rankData);
  const dataRange = maxRank - minRank;

  if (dataRange === 0) {
    const margin = Math.max(10, Math.ceil(maxRank * 0.2));
    const min = Math.max(1, maxRank - margin);
    const max = maxRank + margin;
    const stepSize = Math.max(1, Math.ceil((max - min) / 5));
    return { min, max, stepSize };
  }

  let yMin = Math.max(1, minRank);
  let yMax = maxRank;
  let stepSize;
  const range = yMax - yMin;
  if (range <= 10) stepSize = 1;
  else if (range <= 30) stepSize = Math.max(1, Math.ceil(range / 5));
  else if (range <= 50) stepSize = Math.max(1, Math.ceil(range / 6));
  else if (range <= 100) stepSize = Math.max(1, Math.ceil(range / 7));
  else stepSize = Math.max(1, Math.ceil(range / 8));

  if (maxRank === 500) {
    yMax = 500;
    stepSize = 10;
  }

  return { min: yMin, max: yMax, stepSize };
};

const formatAxisLabel = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const m = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    const h = d.getHours().toString().padStart(2, "0");
    const min = d.getMinutes().toString().padStart(2, "0");
    return `${m}.${day} ${h}:${min}`;
  } catch {
    return "";
  }
};

const formatVolumeAxis = (n) => {
  if (!Number.isFinite(n)) return "";
  if (n >= 10000) return `${Math.round(n / 1000) / 10}만`;
  return n.toLocaleString("ko-KR");
};

const displayRankCell = (rank) => {
  if (rank === 999 || rank === undefined || rank === null) return "500+";
  return rank;
};

const SimpleKeywordRankHistoryModal = ({
  isOpen,
  onClose,
  keywordId,
  keywordWord,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawItems, setRawItems] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [yAxisRange, setYAxisRange] = useState({ min: 1, max: 100 });
  const [volumeData, setVolumeData] = useState([]);
  const [volumeLocked, setVolumeLocked] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    if (!isOpen || keywordId == null) {
      setRawItems([]);
      setChartData([]);
      setTableRows([]);
      setVolumeData([]);
      setVolumeLocked(false);
      setError(null);
      setLoading(false);
      return;
    }

    const id = requestId.current + 1;
    requestId.current = id;

    setLoading(true);
    setError(null);
    setRawItems([]);
    setChartData([]);
    setTableRows([]);
    setVolumeData([]);
    setVolumeLocked(false);

    (async () => {
      try {
        const res = await getSimpleProductKeywordHistoryAPI(keywordId);
        if (requestId.current !== id) return;

        const body = res.data;
        const items =
          body?.items ??
          body?.data?.items ??
          (Array.isArray(body?.data) ? body.data : []);

        const normalized = items
          .map((row) => {
            const dateStr =
              row.created_at || row.updated_at || row.update_at || row.date;
            const rankRaw = row.rank;
            const rankNum =
              rankRaw === undefined || rankRaw === null || rankRaw === ""
                ? NaN
                : Number(rankRaw);
            return {
              date: dateStr,
              rank: Number.isFinite(rankNum) ? rankNum : rankRaw,
              id: row.id ?? row._id,
            };
          })
          .filter((row) => row.date)
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        setRawItems(normalized);

        // 검색량: 서버가 날짜 오름차순으로 내려줌 (최근 30일 합계 일별 스냅샷)
        setVolumeLocked(body?.volume_locked === true);
        setVolumeData(
          (Array.isArray(body?.volumes) ? body.volumes : [])
            .filter((v) => v && v.date && Number.isFinite(Number(v.total_qc)))
            .map((v) => ({
              date: v.date,
              label: String(v.date).slice(5).replace("-", "."),
              total: Number(v.total_qc),
              pc: Number(v.pc_qc) || 0,
              mobile: Number(v.mobile_qc) || 0,
              isLow: v.is_low === true,
            })),
        );

        const forTable = [...normalized].sort(
          (a, b) => new Date(b.date) - new Date(a.date),
        );
        setTableRows(forTable);

        const forChart = normalized.filter(
          (row) =>
            typeof row.rank === "number" &&
            Number.isFinite(row.rank) &&
            row.rank !== 999,
        );
        if (!forChart.length) {
          setChartData([]);
          setYAxisRange({ min: 1, max: 100 });
          return;
        }

        const ranks = forChart.map((r) => r.rank);
        const yRange = calculateYAxisRange(ranks);
        setYAxisRange(yRange);

        const rechartsData = forChart.map((item, index) => ({
          name: `${index + 1}. ${formatAxisLabel(item.date)}`,
          rank: item.rank,
          date: item.date,
          color: getColorByIndex(index, forChart.length),
          index,
        }));
        setChartData(rechartsData);
      } catch (err) {
        if (requestId.current !== id) return;
        console.error("키워드 순위 히스토리 조회 실패:", err);
        setError(
          err.response?.data?.msg ||
            err.message ||
            "순위 기록을 불러오지 못했습니다.",
        );
      } finally {
        if (requestId.current === id) setLoading(false);
      }
    })();
  }, [isOpen, keywordId]);

  if (!isOpen) return null;

  const title =
    keywordWord && String(keywordWord).trim()
      ? `순위 기록 · ${keywordWord}`
      : "순위 기록";

  return (
    <div
      className="rk-simple-kw-history-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="rk-simple-kw-history-popup"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rk-simple-kw-history-title"
      >
        <div className="rk-simple-kw-history-header">
          <h2 id="rk-simple-kw-history-title">{title}</h2>
          <button
            type="button"
            className="rk-simple-kw-history-close"
            onClick={onClose}
            aria-label="닫기"
          />
        </div>

        <div className="rk-simple-kw-history-body">
          {loading && (
            <div className="rk-simple-kw-history-message">불러오는 중…</div>
          )}
          {!loading && error && (
            <div className="rk-simple-kw-history-error">{error}</div>
          )}
          {!loading && !error && rawItems.length === 0 && (
            <div className="rk-simple-kw-history-message">
              저장된 순위 기록이 없습니다.
            </div>
          )}

          {!loading &&
            !error &&
            rawItems.length > 0 &&
            chartData.length === 0 && (
              <div className="rk-simple-kw-history-hint">
                500위 밖(500+) 기록만 있어 차트는 표시되지 않습니다. 아래
                목록에서 확인할 수 있습니다.
              </div>
            )}

          {!loading && !error && chartData.length > 0 && (
            <div className="rk-simple-kw-history-chart-wrap">
              <ResponsiveContainer width="100%" height={320} debounce={50}>
                <LineChart
                  data={chartData}
                  margin={{ top: 16, right: 16, left: 8, bottom: 24 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    strokeOpacity={0.6}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    angle={-35}
                    textAnchor="end"
                    height={72}
                    interval={0}
                    axisLine={{ stroke: "#d1d5db" }}
                    tickLine={{ stroke: "#d1d5db" }}
                  />
                  <YAxis
                    domain={[yAxisRange.min, yAxisRange.max]}
                    reversed
                    hide
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const data = payload[0].payload;
                      const utcDate = new Date(data.date);
                      return (
                        <div className="rk-simple-kw-history-tooltip">
                          <div className="rk-simple-kw-history-tooltip-date">
                            {utcDate.toLocaleString("ko-KR", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                          <div className="rk-simple-kw-history-tooltip-rank">
                            순위:{" "}
                            <strong>
                              {data.rank === 999 ? "500+" : `${data.rank}위`}
                            </strong>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="rank"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ r: 5, fill: "#3b82f6", strokeWidth: 2, stroke: "#fff" }}
                    activeDot={{ r: 7 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {!loading && !error && (
            <div className="rk-simple-kw-history-volume">
              <h3 className="rk-simple-kw-history-section-title">
                검색량 추이 <span>(최근 30일 합계 · 매일 기록)</span>
              </h3>
              {volumeLocked ? (
                <div className="rk-simple-kw-history-hint">
                  🔒 플랜 이용 중에만 검색량 추이를 확인할 수 있습니다.
                </div>
              ) : volumeData.length === 0 ? (
                <div className="rk-simple-kw-history-hint">
                  아직 수집된 검색량이 없습니다. 매일 오전 6시에 기록됩니다.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220} debounce={50}>
                  <LineChart
                    data={volumeData}
                    margin={{ top: 12, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e5e7eb"
                      strokeOpacity={0.6}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: "#6b7280", fontSize: 11 }}
                      axisLine={{ stroke: "#d1d5db" }}
                      tickLine={{ stroke: "#d1d5db" }}
                    />
                    <YAxis
                      width={48}
                      tick={{ fill: "#6b7280", fontSize: 11 }}
                      tickFormatter={formatVolumeAxis}
                      axisLine={false}
                      tickLine={false}
                      domain={["auto", "auto"]}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        const idx = volumeData.findIndex(
                          (v) => v.date === d.date,
                        );
                        const prev = idx > 0 ? volumeData[idx - 1] : null;
                        const diff = prev ? d.total - prev.total : null;
                        return (
                          <div className="rk-simple-kw-history-tooltip">
                            <div className="rk-simple-kw-history-tooltip-date">
                              {d.date}
                            </div>
                            <div className="rk-simple-kw-history-tooltip-rank">
                              검색량:{" "}
                              <strong>
                                {d.total.toLocaleString("ko-KR")}
                                {d.isLow ? " 미만" : ""}
                              </strong>
                              {diff != null && diff !== 0 && (
                                <span>
                                  {" "}
                                  (전일 {diff > 0 ? "+" : ""}
                                  {diff.toLocaleString("ko-KR")})
                                </span>
                              )}
                            </div>
                            <div className="rk-simple-kw-history-tooltip-date">
                              PC {d.pc.toLocaleString("ko-KR")} · 모바일{" "}
                              {d.mobile.toLocaleString("ko-KR")}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="#10b981"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#10b981", strokeWidth: 1.5, stroke: "#fff" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          {!loading && !error && tableRows.length > 0 && (
            <div className="rk-simple-kw-history-table-wrap">
              <table className="rk-simple-kw-history-table">
                <thead>
                  <tr>
                    <th>시간</th>
                    <th>순위</th>
                    <th>키워드</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, idx) => {
                    const utcDate = new Date(row.date);
                    const timeString = utcDate.toLocaleString("ko-KR", {
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    return (
                      <tr key={row.id ?? `${row.date}-${idx}`}>
                        <td>{timeString}</td>
                        <td>{displayRankCell(row.rank)}</td>
                        <td>{keywordWord || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleKeywordRankHistoryModal;
